// Client-side shapes of API responses (snake_case, as on the wire).
export type Role = "instructor" | "participant";

export interface PlanSummary {
  id: string;
  name: string;
  facilitator_name: string;
  starts_on: string;
  ends_on: string;
  access_until: string;
  dimension_code: string;
  institution: { id: string; name: string };
  role: Role;
  access_state: "active" | "expired";
}

export interface PlansResponse {
  data: PlanSummary[];
  meta: { page: number; page_size: number; total: number; instructor_institutions: { id: string; name: string }[] };
}

export const roleLabel: Record<Role, string> = { instructor: "Instrutor", participant: "Participante" };

export interface PlanDetail {
  id: string;
  name: string;
  dimension_code: string;
  facilitator_name: string;
  starts_on: string;
  ends_on: string;
  access_until: string;
  timezone: string;
  version: number;
  institution: { id: string; name: string; logo_version: number | null };
  membership: { id: string; role: Role; groups: { id: string; name: string }[] };
  capabilities: { can_manage: boolean; can_review: boolean };
}

export interface Group {
  id: string;
  name: string;
  version: number;
  archived_at: string | null;
  member_count: number;
  axis_count: number;
  event_count: number;
  /** BLD-28: the group's manager, who validates its maturity categories. */
  manager_membership_id: string | null;
  members: { membership_id: string; name: string; role: Role }[];
}

export interface Axis {
  id: string;
  name: string;
  color: string;
  sort_order: number;
  version: number;
  archived_at: string | null;
  editor_group_id: string | null;
  editor_group_name: string | null;
  objective_count: number;
}

export interface Unit {
  id: string;
  name: string;
  version: number;
  archived_at: string | null;
  initiative_count: number;
}

export interface Membership {
  id: string;
  role: Role;
  active: boolean;
  version: number;
  user_id: string;
  name: string;
  email: string;
  groups: { id: string; name: string }[];
}

export interface Invitation {
  id: string;
  email: string;
  role: Role;
  status: "pending" | "accepted" | "revoked" | "expired";
  delivery_status: "pending" | "sent" | "failed";
  expires_at: string;
  created_at: string;
  invited_by: string;
  groups: { id: string; name: string }[];
}

export interface AuditEvent {
  id: string;
  event_type: string;
  entity_type: string;
  entity_id: string | null;
  occurred_at: string;
  metadata: Record<string, unknown>;
  actor_name: string | null;
}

export const DIMENSION_LABEL = "Gestão de Pessoas";

import type { InitiativeStatus, PendingField } from "../../shared/planning.ts";
export type { InitiativeStatus, PendingField };

export interface InitiativeSummary {
  id: string;
  key_result_id: string;
  title: string;
  status: InitiativeStatus;
  version: number;
  unit_name: string | null;
  action_count: number;
  weight_total: number;
  starts_on: string | null;
  ends_on: string | null;
  revision_number: number | null;
  archived_at: string | null;
  /** Position among active siblings (KR → "Iniciativa n"); null when archived. */
  number: number | null;
  /** Execution progress 0–100 from action progress (meaningful once validated; BLD-17). */
  progress: number;
  reopen_pending: boolean;
}

/** BLD-22: a participant's request to reopen locked work. */
export interface ReopenRequest {
  id: string; entity_type: "initiative" | "objective" | "key_result"; entity_id: string; reason: string;
  status: "pending" | "accepted" | "declined" | "cancelled"; requested_by: string; requested_by_name: string; requested_at: string;
  decided_by_name: string | null; decided_at: string | null; decision_note: string | null; version: number;
  title: string; number: number | null; initiative_id: string | null; axis_id: string; axis_name: string; axis_color: string;
}

export interface ProgressAction {
  id: string; number: number; description: string | null; weight: number | null; starts_on: string | null; ends_on: string | null;
  percent: number; version: number; note: string | null; updated_at: string | null; updated_by: string | null;
}
export interface ProgressDetail {
  initiative_id: string;
  status: InitiativeStatus;
  percent: number;
  actions: ProgressAction[];
  history: { id: string; occurred_at: string; actor_name: string | null; metadata: { changes?: { action_id: string; number: number; from: number; to: number }[]; note?: string | null; percent_from?: number; percent_to?: number } }[];
  can_update: boolean;
  readonly_reason: string | null;
}

export interface StructureKR { id: string; objective_id: string; title: string; description: string | null; version: number; archived_at: string | null; number: number | null; reopen_pending: boolean; initiatives: InitiativeSummary[] }
export interface StructureObjective { id: string; axis_id: string; title: string; description: string | null; version: number; archived_at: string | null; number: number | null; reopen_pending: boolean; key_results: StructureKR[] }
export interface StructureAxis {
  id: string; name: string; color: string; sort_order: number; version: number; archived_at: string | null; number: number | null;
  editor_group_id: string | null; editor_group_name: string | null; can_edit: boolean; objectives: StructureObjective[];
}

export interface ActionItem {
  id?: string | null;
  description: string | null;
  starts_on: string | null;
  ends_on: string | null;
  weight: number | null;
  sort_order?: number;
}

export interface RevisionSummary {
  id: string;
  revision_number: number;
  submitted_at: string;
  submitted_by_name: string;
  decision: "validate" | "request_changes" | null;
  comment: string | null;
  decided_at: string | null;
  decided_by_name: string | null;
}

export interface InitiativeDetail {
  id: string;
  title: string;
  number: number | null;
  description: string | null;
  status: InitiativeStatus;
  version: number;
  archived_at: string | null;
  updated_at: string;
  updated_by: string | null;
  unit: { id: string; name: string | null; archived: boolean } | null;
  context: {
    axis: { id: string; name: string; color: string; editor_group_id: string | null; editor_group_name: string | null; number: number | null };
    objective: { id: string; title: string; description: string | null; number: number | null };
    key_result: { id: string; title: string; description: string | null; number: number | null };
  };
  actions: ActionItem[];
  derived: { starts_on: string | null; ends_on: string | null };
  weight_total: number;
  pending_fields: PendingField[];
  ready_to_submit: boolean;
  current_revision: { id: string; revision_number: number; submitted_at: string } | null;
  return_comment: { comment: string | null; decided_by: string | null; decided_at: string | null } | null;
  revisions: RevisionSummary[];
  reopenings: { occurred_at: string; actor_name: string | null; reason: string | null }[];
  reopen_request: ReopenRequest | null;
  capabilities: { can_edit: boolean; can_submit: boolean; can_review: boolean; can_reopen: boolean; can_archive: boolean; can_request_reopen: boolean };
  readonly_reason: string | null;
}

export interface AgendaEventSummary {
  id: string; name: string; committee_name: string | null; event_on: string | null; target_audience: string | null; target_count: number | null;
  editor_group_id: string; editor_group_name: string; version: number; task_count: number; tasks_start: string | null; tasks_end: string | null;
  complete: boolean; pending_count: number; can_edit: boolean; archived_at: string | null;
}

export interface AgendaTask { id?: string | null; description: string; starts_on: string | null; ends_on: string | null; sort_order?: number }

export interface AgendaEventDetail {
  id: string; name: string; editor_group_id: string; editor_group_name: string; committee_name: string | null; event_on: string | null;
  target_audience: string | null; target_count: number | null; version: number; archived_at: string | null; updated_at: string; updated_by_name: string | null;
  tasks: AgendaTask[]; complete: boolean; pending_fields: PendingField[];
  capabilities: { can_edit: boolean; can_change_group: boolean; can_archive: boolean };
  readonly_reason: string | null;
}
